import {
	alignStats,
	countStatsRecords,
	type DateKey,
	daysBefore,
	DEFAULT_LEVEL,
	type GameState,
	isDateKey,
	type Level,
	parseSavedStacks,
	type PlayerStats,
	type PuzzleModel,
	restoreGame,
	restoreStats,
	type SavedStack,
	serialiseSavedStacks,
	statsRecordCount,
} from '@arrow/game-engine';
import { type FlowState, restoreFlow } from '@arrow/game-engine/flow';

import { browserStore, type KeyValueStore } from './kv.ts';
import { readScheduleStart, SCHEDULE_START_KEY } from './schedule-start.ts';

/**
 * Everything the web app keeps in localStorage, under versioned `arrow:v1:*` keys.
 *
 * Reads are defensive end to end: storage access can fail, JSON can be corrupt, and a
 * blob can come from an older build. The engine's `restore*` functions do the semantic
 * parsing; this layer only adds the keys, JSON and the guarantee that nothing throws.
 */

export const STORAGE_PREFIX = 'arrow:';
const V = 'arrow:v1:';

export const storageKeys = {
	game: (slot: string) => `${V}game:${slot}`,
	flow: (slot: string) => `${V}flow:${slot}`,
	/** A small summary saved beside each game (`ResumeInfo`), so Today can say Continue without the puzzle. */
	resume: (slot: string) => `${V}resume:${slot}`,
	stats: `${V}stats`,
	/** A copy of a stats blob we couldn't fully read, kept rather than silently overwritten. */
	statsBackup: `${V}stats:unreadable`,
	hintSeen: `${V}hint-seen`,
	/** The player's own stacks on Play: names and filter rules only (`{ v: 1, items }`). */
	stacks: `${V}stacks`,
	/** Where the api's schedule starts, as last seen (storage/schedule-start.ts). */
	scheduleStart: SCHEDULE_START_KEY,
	devDate: `${V}dev:date`,
	devPuzzle: `${V}dev:puzzle`,
} as const;

export type SlotSpec =
	| { kind: 'daily'; date: DateKey; level?: Level }
	| { kind: 'practice'; puzzleId: string }
	| { kind: 'puzzle'; puzzleId: string };

/**
 * Where a puzzle attempt is stored:
 * - dailies by date and level, `daily:<date>:<level>` (puzzles can repeat across dates;
 *   no level means `medium`, the only level before levels existed);
 * - practice puzzles by id, `practice:<id>`;
 * - dev puzzles opened by id, `puzzle:<id>`.
 */
export function slotFor(spec: SlotSpec): string {
	switch (spec.kind) {
		case 'daily':
			return `daily:${spec.date}:${spec.level ?? DEFAULT_LEVEL}`;
		case 'practice':
			return `practice:${spec.puzzleId}`;
		case 'puzzle':
			return `puzzle:${spec.puzzleId}`;
	}
}

/** Builds before levels stored a daily as `game:daily:<date>` / `flow:daily:<date>`. */
const LEGACY_DAILY = /^arrow:v1:(game|flow):daily:(\d{4}-\d{2}-\d{2})$/;

/**
 * One-time migration of pre-levels daily saves to their `:medium` slot (every day's,
 * today's included). Idempotent: a key already migrated is skipped, and a legacy key is
 * only removed once its copy is written. Never throws.
 */
export function migrateLegacyKeys(store: KeyValueStore): void {
	try {
		for (const key of store.keys()) {
			const m = LEGACY_DAILY.exec(key);
			if (!m) continue;
			const target = `${V}${m[1]}:${slotFor({ kind: 'daily', date: m[2]!, level: DEFAULT_LEVEL })}`;
			const value = store.get(key);
			if (value === null) continue;
			if (store.get(target) === null && !store.set(target, value)) continue;
			store.remove(key);
		}
	} catch {
		// Leave the old keys where they are; nothing is lost.
	}
}

/** What the Today screen's Play / Continue / See result needs, from saved state alone. */
export interface ResumeInfo {
	status: 'new' | 'started' | 'completed';
	/** Active solving time so far (0 when new). */
	elapsedMs: number;
	answersSolved: number;
	/** null only for a save made before this summary existed (fixed by the next save). */
	answersTotal: number | null;
}

const NEW: ResumeInfo = { status: 'new', elapsedMs: 0, answersSolved: 0, answersTotal: null };

function readJson(store: KeyValueStore, key: string): unknown {
	const raw = store.get(key);
	if (raw === null) return undefined;
	try {
		return JSON.parse(raw) as unknown;
	} catch {
		return undefined;
	}
}

function writeJson(store: KeyValueStore, key: string, value: unknown): boolean {
	try {
		return store.set(key, JSON.stringify(value));
	} catch {
		return false;
	}
}

export interface Persistence {
	loadGame(model: PuzzleModel, slot: string): GameState | null;
	/**
	 * Saves a game. Pass `model` so the resume summary (`resumeInfo`) records the
	 * puzzle's answer count; without it only the game is written.
	 */
	saveGame(slot: string, state: GameState, model?: PuzzleModel): void;
	/** Play / Continue / Solved for a slot, from what's saved (no puzzle needed). Never throws. */
	resumeInfo(slot: string): ResumeInfo;
	/**
	 * Every saved daily and practice game's progress, keyed as the engine's archive keys
	 * them (`2026-10-17:hard`, `practice:<id>`): what Play's lists read. Only slots with a
	 * save are read. Never throws.
	 */
	savedProgress(): Record<string, ResumeInfo>;
	loadFlow(slot: string): FlowState;
	saveFlow(slot: string, flow: FlowState): void;
	/**
	 * The stats, aligned to the schedule as last seen from the api (`alignStats`): days
	 * before its start (e.g. the test environment's 1–24 October, deleted by the numbering
	 * reset) are left out, so they never count toward a streak or a total, and every day's
	 * number comes from its date. Unknown start: everything, as stored.
	 */
	loadStats(): PlayerStats;
	/** Saves the stats; the days `loadStats` left out stay in storage as they were. */
	saveStats(stats: PlayerStats): void;
	hintSeen(): boolean;
	markHintSeen(): void;
	/** The player's own stacks (a bad blob reads as none). Never throws. */
	myStacks(): SavedStack[];
	/** Returns false when the write failed. */
	saveMyStacks(list: readonly SavedStack[]): boolean;
	devDate(): DateKey | null;
	setDevDate(key: DateKey | null): void;
	devPuzzle(): string | null;
	setDevPuzzle(id: string | null): void;
	/** Removes every `arrow:*` key (dev tools). */
	clearAll(): void;
}

export function createPersistence(store: KeyValueStore = browserStore()): Persistence {
	migrateLegacyKeys(store);
	return {
		loadGame(model, slot) {
			try {
				return restoreGame(model, readJson(store, storageKeys.game(slot)));
			} catch {
				return null;
			}
		},
		saveGame(slot, state, model) {
			writeJson(store, storageKeys.game(slot), state);
			if (model) {
				const summary: ResumeSummary = {
					puzzleId: state.puzzleId,
					status: state.completedAt !== null ? 'completed' : state.startedAt !== null ? 'started' : 'new',
					elapsedMs: state.elapsedMs,
					answersSolved: state.solvedAnswers.length,
					answersTotal: model.answerOrder.length,
				};
				writeJson(store, storageKeys.resume(slot), summary);
			}
		},
		resumeInfo(slot) {
			return readResume(store, slot);
		},
		savedProgress() {
			const out: Record<string, ResumeInfo> = {};
			try {
				const prefix = storageKeys.game('');
				for (const key of store.keys()) {
					if (!key.startsWith(prefix)) continue;
					const slot = key.slice(prefix.length);
					const daily = /^daily:(\d{4}-\d{2}-\d{2}):(easy|medium|hard)$/.exec(slot);
					const archiveKey = daily ? `${daily[1]}:${daily[2]}` : slot.startsWith('practice:') ? slot : null;
					if (archiveKey) out[archiveKey] = readResume(store, slot);
				}
			} catch {
				// Nothing saved we can read: every puzzle reads as new.
			}
			return out;
		},
		loadFlow(slot) {
			return restoreFlow(readJson(store, storageKeys.flow(slot)));
		},
		saveFlow(slot, flow) {
			writeJson(store, storageKeys.flow(slot), flow);
		},
		loadStats() {
			const raw = store.get(storageKeys.stats);
			if (raw === null) return restoreStats(undefined);
			let parsed: unknown;
			try {
				parsed = JSON.parse(raw);
			} catch {
				parsed = undefined;
			}
			const stats = restoreStats(parsed);
			// A blob we couldn't fully read is about to be overwritten by the next save.
			// Keep a copy so a streak is never wiped silently.
			if (statsRecordCount(stats) < countStatsRecords(parsed) || (parsed === undefined && raw !== '')) {
				if (store.get(storageKeys.statsBackup) === null) store.set(storageKeys.statsBackup, raw);
			}
			return alignStats(stats, readScheduleStart(store));
		},
		saveStats(stats) {
			// Put back what loadStats left out, untouched: hidden, never deleted.
			const hidden = daysBefore(restoreStats(readJson(store, storageKeys.stats)), readScheduleStart(store));
			const days = Object.keys(hidden).length > 0 ? { ...hidden, ...stats.days } : stats.days;
			writeJson(store, storageKeys.stats, days === stats.days ? stats : { ...stats, days });
		},
		hintSeen() {
			return store.get(storageKeys.hintSeen) === '1';
		},
		markHintSeen() {
			store.set(storageKeys.hintSeen, '1');
		},
		myStacks() {
			return parseSavedStacks(readJson(store, storageKeys.stacks));
		},
		saveMyStacks(list) {
			if (list.length === 0) {
				store.remove(storageKeys.stacks);
				return true;
			}
			return writeJson(store, storageKeys.stacks, serialiseSavedStacks(list));
		},
		devDate() {
			const value = store.get(storageKeys.devDate);
			return isDateKey(value) ? value : null;
		},
		setDevDate(key) {
			if (key === null) store.remove(storageKeys.devDate);
			else if (isDateKey(key)) store.set(storageKeys.devDate, key);
		},
		devPuzzle() {
			const value = store.get(storageKeys.devPuzzle);
			return value && value.length <= 200 ? value : null;
		},
		setDevPuzzle(id) {
			if (id === null) store.remove(storageKeys.devPuzzle);
			else store.set(storageKeys.devPuzzle, id);
		},
		clearAll() {
			for (const key of store.keys()) if (key.startsWith(STORAGE_PREFIX)) store.remove(key);
		},
	};
}

interface ResumeSummary {
	puzzleId: string;
	status: ResumeInfo['status'];
	elapsedMs: number;
	answersSolved: number;
	answersTotal: number;
}

const isCount = (v: unknown): v is number => typeof v === 'number' && Number.isInteger(v) && v >= 0;
const isTime = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v) && v >= 0;

/**
 * Reads the slot's saved game (the truth) and its summary (for the answer count). The
 * summary is trusted only when it belongs to the same puzzle as the saved game.
 */
function readResume(store: KeyValueStore, slot: string): ResumeInfo {
	try {
		const game = readJson(store, storageKeys.game(slot));
		if (typeof game !== 'object' || game === null) return { ...NEW };
		const g = game as Record<string, unknown>;
		const solved = Array.isArray(g.solvedAnswers) ? new Set(g.solvedAnswers.filter((x) => typeof x === 'string')).size : 0;
		const status: ResumeInfo['status'] =
			typeof g.completedAt === 'number' ? 'completed' : typeof g.startedAt === 'number' ? 'started' : 'new';
		const elapsedMs = isTime(g.elapsedMs) ? g.elapsedMs : 0;
		const summary = readJson(store, storageKeys.resume(slot)) as Partial<ResumeSummary> | undefined;
		const answersTotal =
			summary && summary.puzzleId === g.puzzleId && isCount(summary.answersTotal) && summary.answersTotal > 0
				? summary.answersTotal
				: null;
		return {
			status,
			elapsedMs: status === 'new' ? 0 : elapsedMs,
			answersSolved: answersTotal === null ? solved : Math.min(solved, answersTotal),
			answersTotal,
		};
	} catch {
		return { ...NEW };
	}
}
